import React, { FC, useState } from 'react';

import { Modal, Row, Spacer, Text, Title } from '@components/1-atoms';
import { IconButton, Button } from '@components/2-molecules';
import ProductsServicesMenu from '@components/3-organisms/Menus/MenuProductsServices';
import ProductServiceList from '@components/3-organisms/Lists/ListProductService';
import LineItemsView from './LineItemsView';
import LineItemEdit from './LineItemEdit';
import { css } from '@emotion/native';

import Add from '@assets/icons/plus';
import { View, ScrollView } from 'react-native';


export type LineItemType = {
    index: number;
    name: string;
    description: string;
    productServiceId: number;
    price: number;
    taxId: number;
    taxName: string;
    taxRate: number;
    quantity: number;
    subTotal: number;
    discountRate: number;
    discount: number;
    totalBeforeTaxes: number;
    totalTaxes: number;
    total: number;
    documentId?: number;
};

type Props = {
    label?: string;
    data?: Array<any>;
    defaultPriceKey?: string;
    currency: string;
    defaultValue?: LineItemType[];
    onChange?: (value: null | any) => void;
    navigation: any;
};

const LineItems: FC<Props> = ({
    label = 'Add a product or service item',
    defaultValue = [],
    data,
    defaultPriceKey,
    currency,
    onChange,
    navigation,
    ...props
}) => {
    const [lineItems, setLineItems] = useState<LineItemType[]>(defaultValue);
    React.useEffect(() => { onChange?.(lineItems); }, [lineItems]);
    React.useEffect(() => { defaultValue ? setLineItems(defaultValue) : setLineItems([]); }, [defaultValue]);

    const [currentPressLineItemEdit, setCurrentPressedLineItemEdit] = useState<LineItemType | null>(null);

    const [isModalSelectProductServiceVisible, setModalSelectProductServiceVisibility] = useState(false);
    const openModalSelectProductService = () => setModalSelectProductServiceVisibility(true);
    const closeModalSelectProductService = () => setModalSelectProductServiceVisibility(false);

    const [isModalCreateProductServiceMenuVisible, setModalCreateProductServiceMenuVisibility] = useState(false);
    const openModalCreateProductServiceMenu = () => setModalCreateProductServiceMenuVisibility(true);
    const closeModalCreateProductServiceMenu = () => setModalCreateProductServiceMenuVisibility(false);


    const [isModalEditLineItemVisible, setModalLineItemVisibility] = useState(false);
    const openModalEditLineItem = () => setModalLineItemVisibility(true);
    const closeModalEditLineItem = () => setModalLineItemVisibility(false);
    const closeModalAndResetEditLineItem = () => {
        closeModalEditLineItem();
        setCurrentPressedLineItemEdit(null);
    };

    const onCreateProductOrServiceAsLineItem = (productOrService: any) => {
        const lineItem: LineItemType = {
            index: lineItems.length,
            name: productOrService.name,
            description: productOrService.description,
            productServiceId: productOrService.id,
            taxId: productOrService?.saleTax?.id,
            taxName: productOrService?.saleTax?.name,


            price: defaultPriceKey ? productOrService[defaultPriceKey] : 1.00,
            quantity: 1.00,
            taxRate: productOrService?.saleTax?.rate,
            
            subTotal: 0,
            discountRate: 0,
            discount: 0,
            totalBeforeTaxes: 0,
            totalTaxes: 0,
            total: 0,

            documentId: undefined,
        };
    
        // AN EXAMPLE OF A RECEIPT
        // Subtotal:            $ 2.69
        // Discount           - $ 0.54
        // Total Before Taxes   $ 2.15
        // Total Taxes          $ 0.15
        // TOTAL                $ 2.30
        
        lineItem.subTotal = lineItem.price * lineItem.quantity;
        // There is no default discount rate
        lineItem.discountRate = 0.00;
        lineItem.discount = lineItem.subTotal * lineItem.discountRate;
        // subtotal - discount
        lineItem.totalBeforeTaxes = lineItem.subTotal - lineItem.discount;
        // total with discount * tax rate
        lineItem.totalTaxes = lineItem.totalBeforeTaxes * lineItem.taxRate;
        // total with discount + tax(es)
        lineItem.total = lineItem.totalBeforeTaxes + lineItem.totalTaxes;

        closeModalSelectProductService();
        onPressLineItem(lineItem, lineItem.index);
    };

    const onPressLineItem = (lineItem: LineItemType, index: number) => {
        setCurrentPressedLineItemEdit(lineItem);
        openModalEditLineItem();
    };

    const onEditLineItemIndex = (editedLineItem: LineItemType, index: number) => {
        const updatedLineItems = [...lineItems];
        updatedLineItems[index] = editedLineItem;
        setLineItems(updatedLineItems);
        closeModalAndResetEditLineItem();
    };

    const onRemoveLineItemIndex = (removedLineItem: LineItemType, index: number) => {
        const updatedLineItems = lineItems
            // Remove the lineitem with the matched removed index
            .filter((_, filterIndex) => filterIndex !== index)
            // Re-index the line items
            .map((lineItem, index,) => {
                return { ...lineItem, index };
            });
        setLineItems(updatedLineItems);
        closeModalAndResetEditLineItem();
    };

    return (
        <View>
            <Row>
                <Title large bold>{'ITEM(S)'}</Title>{
                    lineItems?.length > 0 &&
                    (
                        <IconButton
                            icon={<Text xsmall bold color="#CC0808">CLEAR</Text>}
                            onPress={() => setLineItems([])}
                        />
                    )
                }
            </Row>

            <LineItemsView
                data={lineItems}
                currency={currency}
                onPress={ (lineItem, index) => onPressLineItem(lineItem, index) }
            />

            <Button
                inset
                bg="transparent"
                align='left'
                color="#434BFF"
                right={<Add stroke="#434BFF" />}
                onPress={openModalSelectProductService}
                {...props}
            >
                {label}
            </Button>

            {/* MODOAL TO SELECT AND ADD A PRODUCT OR SERVICE AS LINE ITEM  */}
            <Modal visible={isModalSelectProductServiceVisible}>
                {/* Custom modal header */}
                <Row style={css`align-items: center;`}>
                    <Spacer h={1} />
                    <Text large bold>{label}</Text>
                    <Button
                        bg="transparent"
                        color="#434BFF"
                        onPress={openModalCreateProductServiceMenu}
                        fullwidth={false}
                        {...props}
                    >
                        New
                    </Button>
                    <Spacer h={1} />
                </Row>

                {/* List of products or services - takes most of the height  */}
                <ScrollView>
                    <ProductServiceList inset data={data} onPress={onCreateProductOrServiceAsLineItem} />
                </ScrollView>

                {/* Custom button to close modal */}
                <View>
                    <Spacer h={1} />
                    <Button inset outlined onPress={closeModalSelectProductService}>CANCEL</Button>
                    <Spacer h={2} />
                </View>
            </Modal>

            {/* MODAL MENU TO CREATE A NON OR INVENTORY PRODUCT ON ANOTHER PAGE */}
            <Modal visible={isModalCreateProductServiceMenuVisible} onClose={closeModalCreateProductServiceMenu}>
                <ProductsServicesMenu navigation={navigation} />
            </Modal>

            {/* MODOAL TO EDIT AND REMOVE A LINE ITEM  */}
            <Modal visible={isModalEditLineItemVisible} title={currentPressLineItemEdit?.name} onClose={closeModalAndResetEditLineItem}>
                {
                    currentPressLineItemEdit ?
                        <LineItemEdit
                            data={currentPressLineItemEdit}
                            onEdit={ (editedLineItem, index) => onEditLineItemIndex(editedLineItem, index) }
                            onRemove={ (removedLineItem, index: number) => onRemoveLineItemIndex(removedLineItem, index) }
                        />
                    :
                    null
                }
            </Modal>
        </View>
    );
};

export default LineItems;
